// ─────────────────────────────────────────────────────────────────────────────
// Page help content — keyed by page id (matches PAGE_MAP in App.jsx).
//
// Each page answers three questions for the viewer:
//   what  → what this page is
//   why   → why THIS role is seeing it   (role-aware: object keyed by role + default)
//   steps → what to do here
//
// Any field may be either a plain value (shared by all roles) or an object keyed
// by role with a `default`. Use pickForRole() to resolve.
// ─────────────────────────────────────────────────────────────────────────────

export const HELP = {
  dashboard: {
    title: "Competitor Show Overview",
    what: "An executive summary of competitor-show lead yield and coverage across all ITE events.",
    why: {
      default: "See how exhibitors found convert into marketing- and sales-qualified leads, and where coverage is thin.",
    },
    steps: [
      "Filter by event or year",
      "Read the KPIs, lead-conversion funnel, and top shows by yield",
      "Expand “Pipeline health” for the operational review breakdown",
    ],
  },

  portfolio: {
    title: "Competitor Shows",
    what: "Every competitor show tracked across your ITE events, grouped by event, with sales & marketing review progress.",
    why: {
      "Sales Head": "Assign shows to Sales Managers and monitor review progress.",
      "Marketing Head": "Assign shows to Marketing Managers and track campaign reviews.",
      "Data Admin": "See enrichment status per show and add new competitor shows.",
      Management: "See companies found and review completeness per event.",
      default: "Find a show and jump into its companies or analytics.",
    },
    steps: [
      "Search or filter by event, year, or stage",
      "Expand an event, then a show, to see review progress",
      "Use “+ Add Competitor Show” to start tracking a new one",
    ],
  },

  companies: {
    title: "Company Master",
    what: "Every AI-researched exhibitor company across your competitor shows, in one searchable list.",
    why: {
      "Sales Manager": "Browse the full company pool alongside the shows assigned to you.",
      "Marketing Manager": "Find and inspect companies to qualify for campaign outreach.",
      default: "Look up any company across all shows — without opening a specific review.",
    },
    steps: [
      "Filter by country, product category, or priority score",
      "Click a company to open its full profile",
    ],
  },

  companyDetail: {
    title: "Company Profile",
    what: "The full AI-researched profile for one company — location and contacts, products, event fit, and global reach.",
    why: {
      "Sales Manager": "Review the evidence behind the AI's event-fit and priority scores before deciding.",
      default: "Understand a single company in depth before deciding whether to pursue it.",
    },
    steps: [
      "Read the Overview, Products & Markets, and Event Intelligence sections",
      "Use 👍 / 👎 on each event match to flag whether the AI matched correctly",
      "Click “Edit Profile” to correct any enrichment error",
    ],
  },

  leads: {
    title: {
      "Marketing Manager": "My Campaign Reviews",
      "Sales Manager": "My Sales Reviews",
      default: "Lead Review",
    },
    what: {
      "Marketing Manager": "Your assigned competitor shows and the leads to qualify for campaigns.",
      default: "Your assigned competitor shows and the companies to review.",
    },
    why: {
      "Sales Head": "Decide which exhibitors are worth pursuing for each assigned show.",
      "Marketing Manager": "Qualify leads and push the good ones to campaign platforms.",
      default: "Work through your assigned shows and record a decision on each company.",
    },
    steps: [
      "Pick an assigned show to open its company list",
      "Set a Review Status on each company (shortlist, reject, refer, …)",
      "A reason is captured when you reject, refer, or flag for research",
    ],
  },

  pipeline: {
    title: {
      "Marketing Head": "Campaign Pipeline",
      "Marketing Manager": "My Campaign Reviews",
      "Sales Manager": "My Sales Reviews",
      default: "Sales Pipeline",
    },
    what: {
      "Marketing Head": "Campaign review progress across every show and your Marketing Managers.",
      default: "Sales review progress across every show and your team.",
    },
    why: {
      "Sales Head": "Monitor workload and completion across your Sales Managers, and create assignments.",
      "Marketing Head": "Monitor campaign review progress and assign shows to Marketing Managers.",
      "Sales Manager": "See your assigned shows and how far along each review is.",
      "Marketing Manager": "See your assigned shows and their push progress.",
      default: "See where review work stands and what still needs attention.",
    },
    steps: [
      "Read the KPIs — awaiting review, in review, complete",
      "Open a show to see or continue its review",
    ],
  },

  insights: {
    title: "Market & Competitor Insights",
    what: "Aggregated market intelligence — country and category heatmaps, plus category gaps.",
    why: {
      Management: "A strategic view of where competitor exhibitors concentrate.",
      default: "Spot which countries and product categories dominate competitor shows.",
    },
    steps: [
      "Read the country × category heatmap",
      "Check category gaps for underserved opportunities",
    ],
  },

  growth: {
    title: "Growth Analysis",
    what: "Year-over-year exhibitor growth per show and event, including country and category trends.",
    why: {
      default: "See which shows, countries, and categories are growing or shrinking over time.",
    },
    steps: [
      "Compare year-over-year totals and growth %",
      "Filter by event, show, or year",
    ],
  },

  quality: {
    title: "Data Quality",
    what: "Data quality issues flagged automatically during data collection, or raised by reviewers.",
    why: {
      "Data Admin": "Your queue — resolve missing fields, duplicates, and enrichment gaps.",
      default: "Keep company records accurate so reviews and campaigns aren't built on bad data.",
    },
    steps: [
      "Filter issues by type, severity, or status",
      "Assign, work, and resolve each issue",
    ],
  },

  send: {
    title: "Send & Export",
    what: "Approved companies ready to push to CRM, plus the export and send history.",
    why: {
      "Sales Head": "Push approved leads to CRM and download lead lists.",
      default: "Get approved companies out of Compass and into your CRM or an Excel file.",
    },
    steps: [
      "Review the approved companies",
      "Send to CRM or export to Excel",
    ],
  },

  admin: {
    title: "Admin",
    what: "Show and enrichment administration — upload exhibitor Excel files and manage runs.",
    why: {
      "Data Admin": "Upload scraped exhibitor lists and trigger enrichment per show.",
      default: "Bring new exhibitor data into Compass so it can be researched and reviewed.",
    },
    steps: [
      "Find the show awaiting upload",
      "Upload the exhibitor Excel to queue enrichment",
    ],
  },
};

// Resolve a field that may be role-aware (object keyed by role, with `default`)
// or a plain shared value (string / array).
export function pickForRole(field, role) {
  if (field == null) return null;
  if (Array.isArray(field)) return field;
  if (typeof field === "object") return field[role] ?? field.default ?? null;
  return field;
}
